import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Flame, AlertTriangle, Shield, CheckCircle,
  AlertCircle, Loader2, RefreshCw, ThumbsUp, ThumbsDown,
  Moon, Cloud, Heart, TrendingUp, ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import AIConsentDialog, { useAIConsent } from '../components/AIConsentDialog';
import { todayLocal } from '../utils/dateUtils';

const FlarePredictions = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  // AI Consent
  const { 
    hasConsent, 
    showConsentDialog, 
    setShowConsentDialog,
    acceptConsent, 
    declineConsent 
  } = useAIConsent();

  useEffect(() => {
    if (hasConsent === true) {
      fetchPrediction();
    } else if (hasConsent === false) {
      setLoading(false);
    } else {
      setShowConsentDialog(true);
      setLoading(false);
    }
  }, [hasConsent]);

  const fetchPrediction = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/flare-predictions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrediction(response.data);
    } catch (error) {
      console.error('Error fetching prediction:', error);
      toast.error('Failed to load prediction');
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async (wasAccurate) => {
    setSubmittingFeedback(true);
    try {
      const today = todayLocal();
      await axios.post(`${API}/flare-predictions/feedback`, null, {
        params: {
          prediction_date: today,
          was_accurate: wasAccurate
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'high':
        return <AlertTriangle className="w-16 h-16 text-red-500" />;
      case 'moderate':
        return <AlertCircle className="w-16 h-16 text-amber-500" />;
      case 'low':
        return <Shield className="w-16 h-16 text-blue-500" />;
      default:
        return <CheckCircle className="w-16 h-16 text-green-500" />;
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'from-red-500/20 to-orange-500/20 border-red-300';
      case 'moderate': return 'from-amber-500/20 to-yellow-500/20 border-amber-300';
      case 'low': return 'from-blue-500/20 to-sky-500/20 border-blue-300';
      default: return 'from-green-500/20 to-emerald-500/20 border-green-300';
    }
  };

  const getRiskTextColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600';
      case 'moderate': return 'text-amber-600';
      case 'low': return 'text-blue-600';
      default: return 'text-green-600';
    }
  };

  const getFactorIcon = (factor) => {
    const lower = factor.toLowerCase();
    if (lower.includes('sleep')) return <Moon className="w-5 h-5 text-indigo-500" />;
    if (lower.includes('weather') || lower.includes('pressure') || lower.includes('humidity')) return <Cloud className="w-5 h-5 text-sky-500" />;
    if (lower.includes('menstrual') || lower.includes('period')) return <Heart className="w-5 h-5 text-pink-500" />;
    if (lower.includes('symptom') || lower.includes('worsening')) return <TrendingUp className="w-5 h-5 text-red-500" />;
    return <AlertCircle className="w-5 h-5 text-amber-500" />;
  };

  const getContributionColor = (contribution) => {
    if (contribution === 'high') return 'bg-red-100 text-red-700 border-red-200';
    if (contribution === 'medium') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-stone-100 text-stone-600 border-stone-200';
  };

  return (
    <div className="space-y-8" data-testid="flare-predictions">
      {/* AI Consent Dialog */}
      <AIConsentDialog
        isOpen={showConsentDialog}
        onAccept={acceptConsent}
        onDecline={declineConsent}
        feature="Flare Predictions"
      />

      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Consent Declined State */}
      {hasConsent === false && (
        <Card className="bg-white rounded-xl border border-stone-100 shadow-sm p-8">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldOff className="w-8 h-8 text-stone-400" />
            </div>
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
              AI Features Disabled
            </h2>
            <p className="text-[#57534E] mb-4">
              Flare Predictions requires AI data sharing. To use this feature, please accept data sharing with OpenAI.
            </p>
            <Button
              onClick={() => setShowConsentDialog(true)}
              className="bg-primary hover:bg-primary/90"
            >
              Review Data Sharing Settings
            </Button>
          </div>
        </Card>
      )}

      {hasConsent === true && (
      <>
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-2">
          <Flame className="w-10 h-10 text-orange-600" strokeWidth={1.5} />
          <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
            Flare Predictions
          </h1>
        </div>
        <p className="text-[#57534E]">
          AI-powered analysis to predict potential flare risk based on your tracked data
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-orange-500 animate-spin" />
          <p className="text-[#57534E]">Analyzing risk factors...</p>
        </div>
      ) : !prediction?.has_prediction ? (
        <Card className="p-8 text-center">
          <Flame className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-[#2D2D2D] mb-2">More Data Needed</h2>
          <p className="text-[#57534E]">{prediction?.message || 'Continue tracking to enable flare predictions!'}</p>
          <p className="text-sm text-[#A8A29E] mt-2">
            We need at least 3 logged flares to identify patterns and make predictions.
          </p>
        </Card>
      ) : (
        <>
          {/* Risk Level Card */}
          <Card className={`p-8 bg-gradient-to-br ${getRiskColor(prediction.risk_level)} border-2`}>
            <div className="flex flex-col md:flex-row items-center gap-6">
              {getRiskIcon(prediction.risk_level)}
              
              <div className="flex-1 text-center md:text-left">
                <p className="text-sm text-[#57534E] uppercase tracking-wide mb-1">Today's Flare Risk</p>
                <h2 className={`text-4xl font-bold capitalize ${getRiskTextColor(prediction.risk_level)}`}>
                  {prediction.risk_level}
                </h2>
                <p className="text-[#57534E] mt-2">{prediction.message}</p>
              </div>
              
              <div className="text-center">
                <div className={`text-5xl font-bold ${getRiskTextColor(prediction.risk_level)}`}>
                  {prediction.risk_score}
                </div>
                <p className="text-sm text-[#A8A29E]">Risk Score</p>
              </div>
            </div>
          </Card>

          {/* Risk Factors */}
          {prediction.risk_factors && prediction.risk_factors.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Active Risk Factors
              </h2>
              
              <div className="space-y-3">
                {prediction.risk_factors.map((factor, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-stone-50 rounded-xl flex items-start gap-3"
                    data-testid={`risk-factor-${index}`}
                  >
                    {getFactorIcon(factor.factor)}
                    <div className="flex-1">
                      <p className="font-bold text-[#2D2D2D]">{factor.factor}</p>
                      <p className="text-sm text-[#57534E]">{factor.details}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full border ${getContributionColor(factor.risk_contribution)}`}>
                      {factor.risk_contribution} risk
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recommendations */}
          {prediction.recommendations && prediction.recommendations.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Recommendations
              </h2>
              
              <ul className="space-y-3">
                {prediction.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[#57534E]">{rec}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Feedback Section */}
          <Card className="p-6 bg-stone-50">
            <h3 className="font-bold text-[#2D2D2D] mb-3">Help Improve Predictions</h3>
            <p className="text-sm text-[#57534E] mb-4">
              At the end of the day, let us know if this prediction was accurate to help improve future predictions.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => submitFeedback(true)}
                disabled={submittingFeedback}
                variant="outline"
                className="flex-1 border-green-200 text-green-700 hover:bg-green-50"
                data-testid="feedback-accurate-btn"
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                Prediction was accurate
              </Button>
              <Button
                onClick={() => submitFeedback(false)}
                disabled={submittingFeedback}
                variant="outline"
                className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
                data-testid="feedback-inaccurate-btn"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Prediction was off
              </Button>
            </div>
          </Card>

          {/* Last Analyzed */}
          <p className="text-center text-sm text-[#A8A29E]">
            Last analyzed: {new Date(prediction.analyzed_at).toLocaleString()}
          </p>
        </>
      )}

      {/* Refresh Button */}
      <div className="text-center">
        <Button
          onClick={fetchPrediction}
          variant="outline"
          disabled={loading}
          data-testid="refresh-prediction-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Prediction
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

export default FlarePredictions;
